import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import DOMPurify from 'dompurify';
import { Colors as C } from '../../constants/Colors.js';
import { supabase } from '../../lib/supabase.js';
import { compressImageForNoticias } from '../../lib/storageService.js';
import { NOTICIA_CONTENIDO_PURIFY } from '../../lib/sanitizeArticleHtml.js';
import { DCard, BtnGold, BtnGhost } from '../components/MarketplaceAtoms.js';
import { adminMenuBadgeCount } from '../../lib/adminMenuBadges.js';
import { ADMIN_SEG } from '../../lib/adminSeguimientoLecturas.js';
import { markNoticiaAdminRead } from '../../lib/noticiasAdminReads.js';
import {
  useAdminSeguimiento,
  AdminLeidaCell,
  AdminSeguimientoMarcarTodas,
  AdminSeguimientoTabLabel,
  adminSeguimientoRowStyle,
} from '../components/AdminSeguimientoLeido.js';
import { AdminAlertDot } from '../../lib/adminMenuBadges.js';
import AdminSocialKitModal from '../components/AdminSocialKitModal.jsx';
import AdminInstagramCarouselBar from '../components/AdminInstagramCarouselBar.jsx';
import { noticiaTelegramCarouselSentInfo } from '../../lib/noticiaTelegramCarousel.js';
import { useAdminCarouselSelection } from '../hooks/useAdminCarouselSelection.js';
import NoticiaImagenCredit from '../components/NoticiaImagenCredit.jsx';
import {
  NOTICIA_IMAGEN_PROCEDENCIA_OPTIONS,
  NOTICIA_IMAGEN_SOURCE,
  applyNoticiaImagenProcedencia,
  getNoticiaImagenOrigenLabel,
  getNoticiaImagenProcedencia,
  mergeNoticiaImagenMetadata,
} from '../../lib/noticiasImagenCredit.js';
import {
  noticiaTieneImagenPreview,
  resolveNoticiaImagenPreviewUrl,
} from '../../lib/noticiasImagenPreview.js';
import { resolveAdminInvokeError } from '../../lib/adminActionsInvoke.js';

const CAT_OPTIONS = ['Vinilo', 'Mercado', 'Eventos', 'Subastas', 'Comunidad'];

function formatFecha(ts) {
  if (!ts) return '—';
  try {
    return new Intl.DateTimeFormat('es-ES', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    }).format(new Date(ts));
  } catch (_) {
    return '—';
  }
}

function formatFechaCorta(iso) {
  if (!iso) return '';
  try {
    return new Intl.DateTimeFormat('es-ES', { day: 'numeric', month: 'short', year: 'numeric' }).format(new Date(iso));
  } catch (_) {
    return '';
  }
}

function dominioFuente(url) {
  const t = String(url ?? '').trim();
  if (!t) return '';
  try {
    const href = /^https?:\/\//i.test(t) ? t : `https://${t}`;
    const host = new URL(href).hostname.replace(/^www\./i, '');
    return host || '';
  } catch (_) {
    return '';
  }
}

/** Formato tipo "06 May 2026" desde texto o ISO guardado en fuente_fecha. */
function formatoFechaOrigenFuente(txt) {
  const s = String(txt ?? '').trim();
  if (!s) return '';
  const parsed = new Date(s);
  if (!Number.isNaN(parsed.getTime())) {
    return new Intl.DateTimeFormat('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    }).format(parsed);
  }
  const m = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (m) {
    const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
    if (!Number.isNaN(d.getTime())) {
      return new Intl.DateTimeFormat('en-GB', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
      }).format(d);
    }
  }
  return '';
}

const NOTICIAS_IMAGENES_BUCKET = 'noticias-imagenes';

/** Path dentro del bucket desde la URL pública .../object/public/noticias-imagenes/<path> */
function storageObjectPathFromNoticiasImagenPublicUrl(url) {
  const s = String(url ?? '').trim();
  if (!s) return null;
  try {
    const u = new URL(s);
    const marker = `/object/public/${NOTICIAS_IMAGENES_BUCKET}/`;
    const i = u.pathname.indexOf(marker);
    if (i === -1) return null;
    const raw = u.pathname.slice(i + marker.length);
    if (!raw) return null;
    return decodeURIComponent(raw);
  } catch {
    return null;
  }
}

const MIME_TO_EXT = {
  'image/jpeg': 'jpg',
  'image/png': 'png',
  'image/gif': 'gif',
  'image/webp': 'webp',
  'video/mp4': 'mp4',
};

const btnMini = {
  padding: '5px 10px',
  borderRadius: 8,
  border: '0.5px solid rgba(200,168,75,0.35)',
  background: 'rgba(200,168,75,0.08)',
  color: C.gold,
  fontSize: 10,
  fontWeight: 600,
  cursor: 'pointer',
};

function noticiaCarouselImagenUrl(row) {
  return resolveNoticiaImagenPreviewUrl(row);
}

function isNoticiaCarouselEligible(row) {
  return !!row?.publicado && noticiaCarouselImagenUrl(row).length > 0;
}

function buildPromptImagenText(row) {
  const titulo = String(row?.titulo ?? '').trim();
  const extracto = String(row?.extracto ?? '').trim();
  const lines = [
    'Create a photorealistic horizontal editorial photo (16:9 aspect ratio) for a music news article.',
    '',
    'HEADLINE — this is the sole subject. The image must depict this headline directly:',
    `"${titulo}"`,
    '',
    'Visual focus:',
    '- Illustrate the headline at a glance: pick the clearest subjects implied by it (artist, band, instrument, vinyl, venue, era, event, crowd, etc.)',
    '- One strong main subject, centered; no unrelated side stories or generic stock-music clichés',
    '- If the headline names a person, place, or object, show that — do not substitute a vague “music” scene',
  ];

  if (extracto) {
    lines.push(
      '',
      'Supporting context only (do not override the headline):',
      extracto,
    );
  }

  lines.push(
    '',
    'Style and technical:',
    '- Photorealistic, high quality, editorial / magazine feel',
    '- Cinematic lighting, rich colors',
    '- No text, logos, watermarks, or readable letters in the image',
    '- Landscape 16:9, minimum 1400×740 px, extra margin at edges for cropping',
  );

  return lines.join('\n');
}

/** Panel curación noticias: borradores / publicadas, acciones y alta manual. */
export default function AdminNoticiasPanel({ user, onError, onSuccess, onBadgesRefresh, menuBadges }) {
  const [tab, setTab] = useState('borradores');
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [busyId, setBusyId] = useState(null);
  const [uploadingImageId, setUploadingImageId] = useState(null);
  const fileInputRef = useRef(null);
  const uploadTargetRef = useRef(null);
  const uploadProcedenciaRef = useRef({
    source: NOTICIA_IMAGEN_SOURCE.DREAMVINYL,
    attributionText: '',
    pexelsUrl: 'https://www.pexels.com/',
  });
  const [uploadProcedenciaModal, setUploadProcedenciaModal] = useState(null);
  const [previewRow, setPreviewRow] = useState(null);
  const [previewEditMode, setPreviewEditMode] = useState(false);
  const [previewSaving, setPreviewSaving] = useState(false);
  const [previewDraft, setPreviewDraft] = useState({
    titulo: '',
    extracto: '',
    categoria: 'Vinilo',
    tiempo_lectura: '',
    imagen_url: '',
    contenido_html: '',
    imagen_procedencia: NOTICIA_IMAGEN_SOURCE.DREAMVINYL,
    imagen_procedencia_text: '',
    imagen_pexels_url: 'https://www.pexels.com/',
  });
  const [promptImagenRow, setPromptImagenRow] = useState(null);
  const [promptImagenCopiado, setPromptImagenCopiado] = useState(false);
  const promptCopiadoTimerRef = useRef(null);
  const [showNewForm, setShowNewForm] = useState(false);
  const [savingNew, setSavingNew] = useState(false);
  /** Kit redes: { contentId, titleHint } */
  const [socialKitOpen, setSocialKitOpen] = useState(null);
  const [socialFlash, setSocialFlash] = useState('');
  const socialFlashTimerRef = useRef(null);
  const [form, setForm] = useState({
    titulo: '',
    extracto: '',
    categoria: 'Vinilo',
    imagen_url: '',
    contenido_html: '',
    tiempo_lectura: '',
    imagen_procedencia: NOTICIA_IMAGEN_SOURCE.DREAMVINYL,
    imagen_procedencia_text: '',
    imagen_pexels_url: 'https://www.pexels.com/',
  });

  const publicadoFlag = tab === 'publicadas';
  const carouselPublicadas = publicadoFlag;
  const carouselSel = useAdminCarouselSelection({ enabled: carouselPublicadas, max: 10 });
  const carouselEligibleRows = useMemo(
    () => (carouselPublicadas ? rows.filter(isNoticiaCarouselEligible) : []),
    [carouselPublicadas, rows],
  );
  const loadReqId = useRef(0);

  useEffect(() => {
    carouselSel.clear();
  }, [tab]); // eslint-disable-line react-hooks/exhaustive-deps
  const noticiasUnreadBadge = adminMenuBadgeCount(menuBadges, 'noticias');
  const borradorRows = publicadoFlag ? [] : rows;
  const seguimiento = useAdminSeguimiento({
    adminId: user?.id,
    section: ADMIN_SEG.NOTICIAS_BORRADOR,
    rows: borradorRows,
    onBadgesRefresh,
    onError,
  });

  const load = useCallback(async () => {
    const reqId = ++loadReqId.current;
    setLoading(true);
    try {
      onError('');
      const { data, error } = await supabase
        .from('noticias_musica')
        .select(
          'id,titulo,extracto,categoria,tiempo_lectura,imagen_url,destacado,publicado,fecha_publicacion,created_at,contenido_html,fuente_url,fuente_fecha,metadata'
        )
        .eq('publicado', publicadoFlag)
        .order(publicadoFlag ? 'fecha_publicacion' : 'created_at', { ascending: false });

      if (reqId !== loadReqId.current) return;

      if (error) {
        onError(error.message || 'No se pudieron cargar las noticias');
        setRows([]);
        return;
      }
      setRows(data || []);
    } catch (e) {
      if (reqId !== loadReqId.current) return;
      onError(e?.message || 'No se pudieron cargar las noticias');
      setRows([]);
    } finally {
      if (reqId === loadReqId.current) setLoading(false);
    }
  }, [onError, publicadoFlag]);

  useEffect(() => {
    load();
  }, [load]);

  const abrirVistaPrevia = row => {
    setPreviewRow(row);
    if (!row?.publicado && row?.id && seguimiento.isUnread(row.id)) {
      void seguimiento.setReadState(row.id, true);
    }
  };

  useEffect(() => {
    if (!previewRow?.id) {
      setPreviewEditMode(false);
      setPreviewSaving(false);
      setPreviewDraft({
        titulo: '',
        extracto: '',
        categoria: 'Vinilo',
        tiempo_lectura: '',
        imagen_url: '',
        contenido_html: '',
        imagen_procedencia: NOTICIA_IMAGEN_SOURCE.DREAMVINYL,
        imagen_procedencia_text: '',
        imagen_pexels_url: 'https://www.pexels.com/',
      });
      return;
    }
    setPreviewEditMode(false);
    setPreviewSaving(false);
    const procedencia = getNoticiaImagenProcedencia(previewRow.metadata);
    setPreviewDraft({
      titulo: String(previewRow.titulo ?? ''),
      extracto: String(previewRow.extracto ?? ''),
      categoria: String(previewRow.categoria ?? 'Vinilo') || 'Vinilo',
      tiempo_lectura: String(previewRow.tiempo_lectura ?? ''),
      imagen_url: String(previewRow.imagen_url ?? ''),
      contenido_html: String(previewRow.contenido_html ?? ''),
      imagen_procedencia: procedencia.source,
      imagen_procedencia_text: procedencia.attributionText,
      imagen_pexels_url: procedencia.pexelsUrl,
    });
  }, [
    previewRow?.id,
    previewRow?.titulo,
    previewRow?.extracto,
    previewRow?.categoria,
    previewRow?.tiempo_lectura,
    previewRow?.imagen_url,
    previewRow?.contenido_html,
    previewRow?.metadata,
  ]);

  const safePreviewHtml = useMemo(
    () =>
      previewRow
        ? DOMPurify.sanitize(
            String((previewEditMode ? previewDraft.contenido_html : previewRow.contenido_html) ?? ''),
            NOTICIA_CONTENIDO_PURIFY
          )
        : '',
    [previewRow, previewDraft.contenido_html, previewEditMode]
  );

  const textoPromptImagen = useMemo(
    () => (promptImagenRow ? buildPromptImagenText(promptImagenRow) : ''),
    [promptImagenRow]
  );

  const guardarEdicionVistaPrevia = async () => {
    const row = previewRow;
    if (!row?.id) return;
    if (previewSaving) return;
    const titulo = String(previewDraft.titulo ?? '').trim();
    if (titulo.length < 3) {
      onError('El título debe tener al menos 3 caracteres.');
      return;
    }
    const categoria = String(previewDraft.categoria ?? '').trim();
    if (!CAT_OPTIONS.includes(categoria)) {
      onError('Categoría no válida.');
      return;
    }
    const imagen_url = String(previewDraft.imagen_url ?? '').trim() || null;
    if (
      imagen_url &&
      previewDraft.imagen_procedencia === NOTICIA_IMAGEN_SOURCE.OTHER &&
      !String(previewDraft.imagen_procedencia_text ?? '').trim()
    ) {
      onError('Indica la procedencia de la imagen (p. ej. Rolling Stone, Wikipedia…).');
      return;
    }
    const patch = {
      titulo,
      extracto: String(previewDraft.extracto ?? '').trim() || null,
      categoria,
      tiempo_lectura: String(previewDraft.tiempo_lectura ?? '').trim() || null,
      imagen_url,
      contenido_html: String(previewDraft.contenido_html ?? '').trim() || '',
      metadata: imagen_url
        ? applyNoticiaImagenProcedencia(row.metadata, imagen_url, {
            source: previewDraft.imagen_procedencia,
            attributionText: previewDraft.imagen_procedencia_text,
            pexelsUrl: previewDraft.imagen_pexels_url,
          })
        : mergeNoticiaImagenMetadata(row.metadata, imagen_url),
    };
    setPreviewSaving(true);
    onError('');
    try {
      const { error } = await supabase.from('noticias_musica').update(patch).eq('id', row.id);
      if (error) throw error;
      setRows(prev => prev.map(r => (r.id === row.id ? { ...r, ...patch } : r)));
      setPreviewRow(p => (p?.id === row.id ? { ...p, ...patch } : p));
      setPreviewEditMode(false);
    } catch (e) {
      onError(e?.message || 'No se pudo guardar la edición.');
    } finally {
      setPreviewSaving(false);
    }
  };

  useEffect(() => {
    return () => {
      if (promptCopiadoTimerRef.current) clearTimeout(promptCopiadoTimerRef.current);
      if (socialFlashTimerRef.current) clearTimeout(socialFlashTimerRef.current);
    };
  }, []);

  const abrirKitRedes = row => {
    if (!row?.id) return;
    setSocialKitOpen({ contentId: row.id, titleHint: row.titulo || '' });
  };

  const abrirPromptImagen = row => {
    setPromptImagenCopiado(false);
    setPromptImagenRow(row);
  };

  const copiarPromptImagen = async () => {
    const t = textoPromptImagen;
    if (!t) return;
    try {
      await navigator.clipboard.writeText(t);
    } catch {
      try {
        const ta = document.createElement('textarea');
        ta.value = t;
        ta.style.position = 'fixed';
        ta.style.left = '-9999px';
        document.body.appendChild(ta);
        ta.focus();
        ta.select();
        document.execCommand('copy');
        document.body.removeChild(ta);
      } catch (_) {
        onError('No se pudo copiar al portapapeles');
        return;
      }
    }
    setPromptImagenCopiado(true);
    if (promptCopiadoTimerRef.current) clearTimeout(promptCopiadoTimerRef.current);
    promptCopiadoTimerRef.current = setTimeout(() => setPromptImagenCopiado(false), 2500);
  };

  const publicar = async row => {
    setBusyId(row.id);
    onError('');
    try {
      const { data, error } = await supabase.functions.invoke('admin-actions', {
        body: { action: 'news_publish', payload: { noticia_id: row.id, titulo: row?.titulo ?? null, categoria: row?.categoria ?? null } },
      });
      if (error || !data?.ok) {
        throw new Error(await resolveAdminInvokeError(error, data, 'No se pudo publicar la noticia.'));
      }
      if (data?.imagen_persisted) {
        onSuccess?.(
          data?.imagen_compressed
            ? 'Imagen guardada y optimizada en el servidor (WebP). Noticia publicada.'
            : 'Imagen guardada en el servidor. Noticia publicada.',
        );
      } else if (data?.telegram_auto?.ok) {
        onSuccess?.('Noticia publicada y enviada a Telegram automáticamente.');
      } else if (data?.telegram_auto && !data.telegram_auto.ok) {
        onSuccess?.('Noticia publicada.');
        onError?.(
          data.telegram_auto.queued
            ? `${data.telegram_auto.message || 'Telegram falló.'} Reintenta en Admin → Cola redes → Procesar cola.`
            : data.telegram_auto.message ||
                'Telegram automático falló. Usa Redes → Telegram o revisa n8n.',
        );
      } else {
        onSuccess?.('Noticia publicada.');
      }
      await load();
      onBadgesRefresh?.();
    } catch (e) {
      onError(e?.message || 'No se pudo publicar');
    } finally {
      setBusyId(null);
    }
  };

  const pasarABorrador = async row => {
    if (
      !window.confirm(
        '¿Pasar esta noticia a borrador?\n\nDejará de verse en la web pública hasta que vuelvas a publicarla. Podrás subir imagen y corregir la URL desde Borradores.'
      )
    ) {
      return;
    }
    setBusyId(row.id);
    onError('');
    try {
      const { data, error } = await supabase.functions.invoke('admin-actions', {
        body: { action: 'news_to_draft', payload: { noticia_id: row.id, titulo: row?.titulo ?? null } },
      });
      if (error || !data?.ok) {
        throw new Error(await resolveAdminInvokeError(error, data, 'No se pudo pasar a borrador.'));
      }
      if (previewRow?.id === row.id) setPreviewRow(null);
      onSuccess?.('Noticia en borradores. Puedes subir imagen y volver a publicar.');
      setTab('borradores');
      onBadgesRefresh?.();
    } catch (e) {
      onError(e?.message || 'No se pudo pasar a borrador');
    } finally {
      setBusyId(null);
    }
  };

  const toggleDestacar = async row => {
    const next = !row.destacado;
    setBusyId(row.id);
    onError('');
    try {
      const { data, error } = await supabase.functions.invoke('admin-actions', {
        body: { action: 'news_set_featured', payload: { noticia_id: row.id, destacado: next, titulo: row?.titulo ?? null } },
      });
      if (error || !data?.ok) {
        throw new Error(await resolveAdminInvokeError(error, data, 'No se pudo actualizar destacado.'));
      }
      setRows(prev => prev.map(x => (x.id === row.id ? { ...x, destacado: next } : x)));
      setPreviewRow(p => (p?.id === row.id ? { ...p, destacado: next } : p));
      onBadgesRefresh?.();
    } catch (e) {
      onError(e?.message || 'No se pudo actualizar destacado');
    } finally {
      setBusyId(null);
    }
  };

  const eliminar = async row => {
    if (!window.confirm('¿Eliminar esta noticia de forma permanente?')) return;
    setBusyId(row.id);
    onError('');
    try {
      const objectPath = storageObjectPathFromNoticiasImagenPublicUrl(row.imagen_url);
      if (objectPath) {
        const { error: stErr } = await supabase.storage.from(NOTICIAS_IMAGENES_BUCKET).remove([objectPath]);
        if (stErr) throw stErr;
      }
      const { data, error } = await supabase.functions.invoke('admin-actions', {
        body: { action: 'news_delete', payload: { noticia_id: row.id, titulo: row?.titulo ?? null } },
      });
      if (error || !data?.ok) {
        throw new Error(await resolveAdminInvokeError(error, data, 'No se pudo eliminar la noticia.'));
      }
      if (previewRow?.id === row.id) setPreviewRow(null);
      await load();
      onBadgesRefresh?.();
    } catch (e) {
      onError(e?.message || 'No se pudo eliminar');
    } finally {
      setBusyId(null);
    }
  };

  const submitNew = async () => {
    const titulo = form.titulo.trim();
    if (!titulo) {
      onError('El título es obligatorio');
      return;
    }
    const imagen_url = form.imagen_url.trim() || null;
    if (
      imagen_url &&
      form.imagen_procedencia === NOTICIA_IMAGEN_SOURCE.OTHER &&
      !form.imagen_procedencia_text.trim()
    ) {
      onError('Indica la procedencia de la imagen (p. ej. Rolling Stone, Wikipedia…).');
      return;
    }
    setSavingNew(true);
    onError('');
    try {
      const metadata = imagen_url
        ? applyNoticiaImagenProcedencia(null, imagen_url, {
            source: form.imagen_procedencia,
            attributionText: form.imagen_procedencia_text,
            pexelsUrl: form.imagen_pexels_url,
          })
        : null;
      const { data: inserted, error } = await supabase
        .from('noticias_musica')
        .insert({
          titulo,
          extracto: form.extracto.trim() || null,
          categoria: form.categoria,
          imagen_url,
          contenido_html: form.contenido_html.trim() || '',
          tiempo_lectura: form.tiempo_lectura.trim() || null,
          metadata,
          publicado: false,
        })
        .select('id')
        .single();
      if (error) throw error;
      if (inserted?.id && user?.id) {
        await markNoticiaAdminRead(user.id, inserted.id);
      }
      setShowNewForm(false);
      setForm({
        titulo: '',
        extracto: '',
        categoria: 'Vinilo',
        imagen_url: '',
        contenido_html: '',
        tiempo_lectura: '',
        imagen_procedencia: NOTICIA_IMAGEN_SOURCE.DREAMVINYL,
        imagen_procedencia_text: '',
        imagen_pexels_url: 'https://www.pexels.com/',
      });
      setTab('borradores');
      await load();
      onBadgesRefresh?.();
    } catch (e) {
      onError(e?.message || 'No se pudo crear la noticia');
    } finally {
      setSavingNew(false);
    }
  };

  const validarProcedenciaModal = modal => {
    if (!modal?.row?.id) return 'Borrador no válido.';
    if (
      modal.source === NOTICIA_IMAGEN_SOURCE.OTHER &&
      !String(modal.attributionText ?? '').trim()
    ) {
      return 'Indica la procedencia de la imagen (p. ej. Rolling Stone, Wikipedia…).';
    }
    return null;
  };

  const procedenciaDesdeModal = modal => ({
    source: modal.source,
    attributionText: String(modal.attributionText ?? '').trim(),
    pexelsUrl: String(modal.pexelsUrl ?? '').trim() || 'https://www.pexels.com/',
  });

  const subirImagenBorradorClick = row => {
    if (!row?.id) return;
    const procedencia = getNoticiaImagenProcedencia(row.metadata);
    setUploadProcedenciaModal({
      row,
      imagenUrl: String(row.imagen_url ?? '').trim(),
      source: procedencia.source,
      attributionText: procedencia.attributionText,
      pexelsUrl: procedencia.pexelsUrl,
    });
  };

  const guardarImagenUrlDesdeModal = async () => {
    const modal = uploadProcedenciaModal;
    const validacion = validarProcedenciaModal(modal);
    if (validacion) {
      onError(validacion);
      return;
    }
    const imagen_url = String(modal.imagenUrl ?? '').trim();
    if (!imagen_url) {
      onError('Indica la URL de la imagen o usa «Elegir archivo».');
      return;
    }
    try {
      const href = imagen_url.startsWith('//') ? `https:${imagen_url}` : imagen_url;
      const u = new URL(href);
      if (!/^https?:$/i.test(u.protocol)) throw new Error('protocol');
    } catch {
      onError('URL de imagen no válida.');
      return;
    }

    const row = modal.row;
    setUploadingImageId(row.id);
    onError('');
    try {
      const proc = procedenciaDesdeModal(modal);
      const { data, error } = await supabase.functions.invoke('admin-actions', {
        body: {
          action: 'news_save_imagen',
          payload: {
            noticia_id: row.id,
            imagen_url,
            imagen_source: proc.source,
            attribution_text: proc.attributionText || null,
            pexels_url: proc.pexelsUrl || null,
            titulo: row?.titulo ?? null,
          },
        },
      });
      if (error || !data?.ok) {
        throw new Error(await resolveAdminInvokeError(error, data, 'No se pudo guardar la imagen.'));
      }
      const imagenUrlFinal = data.imagen_url || imagen_url;
      const metadata = data.metadata ?? applyNoticiaImagenProcedencia(row.metadata, imagenUrlFinal, proc);
      setRows(prev => prev.map(x => (x.id === row.id ? { ...x, imagen_url: imagenUrlFinal, metadata } : x)));
      setPreviewRow(p => (p?.id === row.id ? { ...p, imagen_url: imagenUrlFinal, metadata } : p));
      setUploadProcedenciaModal(null);
      onSuccess?.(
        data.persisted
          ? 'Imagen descargada, comprimida y guardada en el servidor.'
          : 'Imagen guardada.'
      );
    } catch (err) {
      onError(err?.message || 'No se pudo guardar la imagen');
    } finally {
      setUploadingImageId(null);
    }
  };

  const confirmarProcedenciaYSubirImagen = () => {
    const modal = uploadProcedenciaModal;
    const validacion = validarProcedenciaModal(modal);
    if (validacion) {
      onError(validacion);
      return;
    }
    uploadTargetRef.current = modal.row;
    uploadProcedenciaRef.current = procedenciaDesdeModal(modal);
    setUploadProcedenciaModal(null);
    onError('');
    fileInputRef.current?.click();
  };

  const onArchivoNoticiaSeleccionado = async e => {
    const row = uploadTargetRef.current;
    uploadTargetRef.current = null;
    const input = e.target;
    const file = input.files?.[0];
    input.value = '';
    if (!file || !row?.id) return;

    const name = String(file.name || '');
    const extByMime = file.type ? MIME_TO_EXT[file.type] : null;
    const extByName = /\.(jpe?g)$/i.test(name)
      ? 'jpg'
      : /\.png$/i.test(name)
        ? 'png'
        : /\.webp$/i.test(name)
          ? 'webp'
          : /\.gif$/i.test(name)
            ? 'gif'
            : /\.mp4$/i.test(name)
              ? 'mp4'
              : '';
    if (file.type && !extByMime) {
      onError('Formato no permitido. Usa JPG, PNG, GIF o MP4.');
      return;
    }
    const ext = extByMime || extByName;
    if (!ext) {
      onError('Formato no permitido. Usa JPG, PNG, GIF o MP4.');
      return;
    }
    if (extByMime && extByName && extByMime !== extByName) {
      onError('El tipo del archivo no coincide con la extensión.');
      return;
    }

    setUploadingImageId(row.id);
    onError('');
    try {
      const uploadFile =
        ext === 'gif' || ext === 'mp4' ? file : await compressImageForNoticias(file);
      const uploadExt =
        (uploadFile.name && uploadFile.name.includes('.') ? uploadFile.name.split('.').pop() : '') || ext;
      const safeExt = /^[a-z0-9]+$/i.test(uploadExt) ? uploadExt.toLowerCase() : ext;
      const objectPath = `${row.id}-${Date.now()}.${safeExt}`;
      const contentType =
        uploadFile.type ||
        (safeExt === 'mp4'
          ? 'video/mp4'
          : safeExt === 'jpg'
            ? 'image/jpeg'
            : safeExt === 'png'
              ? 'image/png'
              : safeExt === 'webp'
                ? 'image/webp'
                : 'image/gif');
      const { error: upErr } = await supabase.storage.from(NOTICIAS_IMAGENES_BUCKET).upload(objectPath, uploadFile, {
        contentType,
        upsert: false,
      });
      if (upErr) throw upErr;

      const { data: pub } = supabase.storage.from(NOTICIAS_IMAGENES_BUCKET).getPublicUrl(objectPath);
      const imagen_url = pub?.publicUrl;
      if (!imagen_url) throw new Error('No se obtuvo URL pública');

      const procedencia = uploadProcedenciaRef.current;
      uploadProcedenciaRef.current = {
        source: NOTICIA_IMAGEN_SOURCE.DREAMVINYL,
        attributionText: '',
        pexelsUrl: 'https://www.pexels.com/',
      };
      const metadata = applyNoticiaImagenProcedencia(row.metadata, imagen_url, procedencia);
      const { error: dbErr } = await supabase.from('noticias_musica').update({ imagen_url, metadata }).eq('id', row.id);
      if (dbErr) throw dbErr;

      setRows(prev => prev.map(x => (x.id === row.id ? { ...x, imagen_url, metadata } : x)));
      setPreviewRow(p => (p?.id === row.id ? { ...p, imagen_url, metadata } : p));
      onSuccess?.('Imagen subida y comprimida en el servidor.');
    } catch (err) {
      onError(err?.message || 'No se pudo subir la imagen');
    } finally {
      setUploadingImageId(null);
    }
  };

  return (
    <div>
      <input
        ref={fileInputRef}
        type="file"
        accept="image/jpeg,image/png,image/webp,image/gif,video/mp4,.jpg,.jpeg,.png,.webp,.gif,.mp4"
        style={{ display: 'none' }}
        onChange={onArchivoNoticiaSeleccionado}
      />
      {socialFlash ? (
        <div
          style={{
            marginBottom: 12,
            padding: '10px 14px',
            borderRadius: 10,
            background: 'rgba(29,158,117,0.12)',
            border: '0.5px solid rgba(29,158,117,0.35)',
            color: '#9fe8ca',
            fontSize: 12,
            fontWeight: 600,
          }}
        >
          {socialFlash}
        </div>
      ) : null}
      <DCard style={{ padding: '14px 16px', marginBottom: 14, display: 'flex', flexWrap: 'wrap', gap: 10, alignItems: 'center' }}>
        <button
          type="button"
          onClick={() => setTab('borradores')}
          style={{
            padding: '8px 12px',
            borderRadius: 8,
            border: 'none',
            cursor: 'pointer',
            fontSize: 12,
            fontWeight: tab === 'borradores' ? 700 : 500,
            color: tab === 'borradores' ? C.gold : 'rgba(255,255,255,0.5)',
            background: tab === 'borradores' ? 'rgba(200,168,75,0.12)' : 'transparent',
            display: 'inline-flex',
            alignItems: 'center',
            gap: 6,
          }}
        >
          <AdminSeguimientoTabLabel
            label="Borradores"
            unreadCount={noticiasUnreadBadge}
            title={`${noticiasUnreadBadge} borrador${noticiasUnreadBadge === 1 ? '' : 'es'} sin leer`}
          />
        </button>
        <AdminSeguimientoMarcarTodas
          unreadCount={noticiasUnreadBadge}
          markingAllRead={seguimiento.markingAllRead}
          onMarcarTodas={seguimiento.marcarTodasLeidas}
        />
        <button
          type="button"
          onClick={() => setTab('publicadas')}
          title="Publicadas: selección múltiple y envío carrusel a Telegram"
          style={{
            padding: '8px 12px',
            borderRadius: 8,
            border: tab === 'publicadas' ? `0.5px solid ${C.gold}` : '0.5px solid rgba(255,255,255,0.12)',
            cursor: 'pointer',
            fontSize: 12,
            fontWeight: tab === 'publicadas' ? 700 : 500,
            color: tab === 'publicadas' ? C.gold : 'rgba(255,255,255,0.5)',
            background: tab === 'publicadas' ? 'rgba(200,168,75,0.12)' : 'rgba(255,255,255,0.04)',
          }}
        >
          Publicadas · carrusel Telegram
        </button>
        <BtnGold type="button" style={{ width: 'auto', minWidth: 160, marginLeft: 'auto' }} onClick={() => setShowNewForm(true)}>
          Nueva noticia manual
        </BtnGold>
        <BtnGhost type="button" style={{ fontSize: 11 }} onClick={() => load()}>
          Recargar
        </BtnGhost>
      </DCard>
      {!publicadoFlag ? (
        <p style={{ fontSize: 11, color: 'rgba(255,255,255,0.42)', margin: '-6px 0 12px', lineHeight: 1.45 }}>
          Marca cada borrador como leído cuando lo revises. Los avisos del menú admin solo cuentan borradores sin leer.
        </p>
      ) : null}

      {carouselPublicadas ? (
        <>
          <p style={{ fontSize: 11, color: 'rgba(255,255,255,0.42)', margin: '0 0 12px', lineHeight: 1.45 }}>
            Marca noticias con imagen (máx. 10) y «Enviar carrusel a Telegram». La columna <strong>Carrusel TG</strong>{' '}
            indica si ya se enviaron en un carrusel anterior.
          </p>
          <AdminInstagramCarouselBar
            tipo="noticias"
            selection={carouselSel}
            onError={onError}
            onSuccess={msg => {
              onSuccess?.(msg);
              load();
            }}
          />
        </>
      ) : null}

      <DCard style={{ padding: 0, overflow: 'hidden' }}>
        {loading ? (
          <div style={{ padding: 20, fontSize: 13, color: 'rgba(255,255,255,0.45)' }}>Cargando…</div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table
              style={{
                width: '100%',
                borderCollapse: 'collapse',
                fontSize: 11,
                minWidth: 1020,
              }}
            >
              <thead>
                <tr style={{ textAlign: 'left', color: 'rgba(255,255,255,0.45)', borderBottom: '1px solid rgba(255,255,255,0.1)' }}>
                  {carouselPublicadas ? (
                    <th style={{ padding: '10px 12px', width: 40 }} title="Selección carrusel Telegram (noticias con imagen)">
                      <input
                        type="checkbox"
                        aria-label="Seleccionar todas las noticias con imagen"
                        checked={
                          carouselEligibleRows.length > 0 &&
                          carouselEligibleRows.every(r => carouselSel.isSelected(r.id))
                        }
                        onChange={() => carouselSel.toggleAll(carouselEligibleRows.map(r => r.id))}
                      />
                    </th>
                  ) : null}
                  {!publicadoFlag ? (
                    <th style={{ padding: '10px 12px', width: 72 }} title="Seguimiento de revisión">
                      Leída
                    </th>
                  ) : null}
                  <th style={{ padding: '10px 12px' }}>Título</th>
                  <th style={{ padding: '10px 12px' }}>Categoría</th>
                  <th style={{ padding: '10px 12px' }}>Fecha</th>
                  <th style={{ padding: '10px 12px' }}>Origen</th>
                  <th style={{ padding: '10px 12px' }}>Fecha origen</th>
                  {publicadoFlag ? (
                    <th style={{ padding: '10px 12px', whiteSpace: 'nowrap' }} title="Carrusel Telegram enviado desde admin">
                      Carrusel TG
                    </th>
                  ) : null}
                  {publicadoFlag ? <th style={{ padding: '10px 12px' }}>Destacado</th> : null}
                  <th style={{ padding: '10px 12px' }}>Tiempo lectura</th>
                  <th style={{ padding: '10px 12px' }}>Acciones</th>
                </tr>
              </thead>
              <tbody>
                {rows.map(r => {
                  const fechaCol = publicadoFlag ? r.fecha_publicacion : r.created_at;
                  const celdaOrigen = dominioFuente(r.fuente_url) || '—';
                  const celdaFechaOrigen = formatoFechaOrigenFuente(r.fuente_fecha) || '—';
                  const leida = seguimiento.readIds.has(r.id);
                  const sinLeer = !publicadoFlag && seguimiento.isUnread(r.id);
                  const carouselEligible = carouselPublicadas && isNoticiaCarouselEligible(r);
                  const carouselSent = publicadoFlag ? noticiaTelegramCarouselSentInfo(r) : null;
                  return (
                    <tr
                      key={r.id}
                      style={adminSeguimientoRowStyle(sinLeer, {
                        borderBottom: '1px solid rgba(255,255,255,0.06)',
                        color: '#fff',
                        ...(carouselSel.isSelected(r.id) ? { background: 'rgba(200,168,75,0.06)' } : {}),
                      })}
                    >
                      {carouselPublicadas ? (
                        <td style={{ padding: '10px 12px', verticalAlign: 'middle' }}>
                          {carouselEligible ? (
                            <input
                              type="checkbox"
                              aria-label={`Seleccionar ${r.titulo}`}
                              checked={carouselSel.isSelected(r.id)}
                              disabled={!carouselSel.isSelected(r.id) && carouselSel.atMax}
                              onChange={() => carouselSel.toggle(r.id)}
                            />
                          ) : (
                            <span style={{ color: 'rgba(255,255,255,0.25)', fontSize: 10 }} title="Sin imagen">
                              —
                            </span>
                          )}
                        </td>
                      ) : null}
                      {!publicadoFlag ? (
                        <td style={{ padding: '10px 12px', verticalAlign: 'middle' }}>
                          <AdminLeidaCell
                            leida={leida}
                            busy={seguimiento.readBusyId === r.id}
                            onChange={checked => seguimiento.setReadState(r.id, checked)}
                          />
                        </td>
                      ) : null}
                      <td
                        style={{
                          padding: '10px 12px',
                          maxWidth: 220,
                          fontWeight: sinLeer ? 800 : 600,
                          color: sinLeer ? '#fff' : '#f2f2f2',
                        }}
                      >
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                          {sinLeer ? <AdminAlertDot title="Borrador sin leer" size={7} /> : null}
                          <span>{r.titulo}</span>
                        </span>
                      </td>
                      <td style={{ padding: '10px 12px', color: C.gold }}>{r.categoria}</td>
                      <td style={{ padding: '10px 12px', whiteSpace: 'nowrap', fontSize: 10 }}>{formatFecha(fechaCol)}</td>
                      <td
                        style={{
                          padding: '10px 12px',
                          maxWidth: 160,
                          fontSize: 10,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                          color: 'rgba(255,255,255,0.85)',
                        }}
                        title={celdaOrigen !== '—' ? celdaOrigen : undefined}
                      >
                        {celdaOrigen}
                      </td>
                      <td style={{ padding: '10px 12px', whiteSpace: 'nowrap', fontSize: 10 }}>
                        {celdaFechaOrigen}
                      </td>
                      {publicadoFlag ? (
                        <td style={{ padding: '10px 12px', whiteSpace: 'nowrap' }}>
                          {carouselSent?.sent ? (
                            <span style={{ color: C.green }} title={formatFecha(carouselSent.at)}>
                              Sí
                            </span>
                          ) : (
                            <span style={{ color: 'rgba(255,255,255,0.4)' }}>No</span>
                          )}
                        </td>
                      ) : null}
                      {publicadoFlag ? (
                        <td style={{ padding: '10px 12px' }}>{r.destacado ? 'Sí' : 'No'}</td>
                      ) : null}
                      <td style={{ padding: '10px 12px' }}>{r.tiempo_lectura?.trim() || '—'}</td>
                      <td style={{ padding: '10px 12px' }}>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                          {!publicadoFlag ? (
                            <button type="button" disabled={busyId === r.id} style={btnMini} onClick={() => abrirPromptImagen(r)}>
                              Prompt imagen
                            </button>
                          ) : null}
                          {!publicadoFlag ? (
                            <button
                              type="button"
                              disabled={busyId === r.id || uploadingImageId === r.id}
                              style={{ ...btnMini, opacity: uploadingImageId === r.id ? 0.55 : 1 }}
                              onClick={() => subirImagenBorradorClick(r)}
                            >
                              {uploadingImageId === r.id ? 'Subiendo…' : 'Subir imagen'}
                            </button>
                          ) : null}
                          <button type="button" disabled={busyId === r.id} style={btnMini} onClick={() => abrirVistaPrevia(r)}>
                            Vista previa
                          </button>
                          {!r.publicado ? (
                            <button
                              type="button"
                              disabled={busyId === r.id}
                              onClick={() => publicar(r)}
                              style={{ ...btnMini, borderColor: 'rgba(29,158,117,0.45)', color: '#5DCAA5' }}
                            >
                              Publicar
                            </button>
                          ) : null}
                          {publicadoFlag ? (
                            <button
                              type="button"
                              disabled={busyId === r.id}
                              style={{ ...btnMini, borderColor: 'rgba(200,168,75,0.45)', color: C.gold }}
                              onClick={() => pasarABorrador(r)}
                              title="Revocar publicación: vuelve a borradores para corregir imagen y volver a publicar"
                            >
                              Pasar a borrador
                            </button>
                          ) : null}
                          {publicadoFlag ? (
                            <button
                              type="button"
                              disabled={busyId === r.id}
                              style={
                                r.destacado
                                  ? {
                                      ...btnMini,
                                      border: '1px solid rgba(29,158,117,0.65)',
                                      background: 'rgba(29,158,117,0.25)',
                                      color: '#9fe8ca',
                                    }
                                  : btnMini
                              }
                              onClick={() => toggleDestacar(r)}
                            >
                              {r.destacado ? 'Destacada ✓' : 'Destacar'}
                            </button>
                          ) : null}
                          {publicadoFlag ? (
                            <button
                              type="button"
                              disabled={busyId === r.id}
                              style={{ ...btnMini, borderColor: 'rgba(120,160,255,0.45)', color: 'rgba(180,200,255,0.95)' }}
                              onClick={() => abrirKitRedes(r)}
                            >
                              Redes
                            </button>
                          ) : null}
                          <button
                            type="button"
                            disabled={busyId === r.id}
                            onClick={() => eliminar(r)}
                            style={{ ...btnMini, borderColor: 'rgba(226,75,74,0.45)', color: C.danger }}
                          >
                            Eliminar
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            {!rows.length ? (
              <div style={{ padding: 16, fontSize: 12, color: 'rgba(255,255,255,0.35)' }}>
                No hay noticias en esta pestaña.
              </div>
            ) : null}
          </div>
        )}
      </DCard>

      {previewRow ? (
        <div
          role="dialog"
          aria-modal="true"
          aria-label="Vista previa"
          style={{
            position: 'fixed',
            inset: 0,
            zIndex: 2000,
            background: 'rgba(10,10,15,0.72)',
            display: 'flex',
            alignItems: 'flex-start',
            justifyContent: 'center',
            padding: '24px 16px',
            overflowY: 'auto',
          }}
          onClick={() => setPreviewRow(null)}
        >
          <div
            role="presentation"
            onClick={e => e.stopPropagation()}
            style={{
              width: '100%',
              maxWidth: 680,
              background: '#fff',
              borderRadius: 14,
              boxShadow: '0 22px 60px rgba(0,0,0,0.35)',
              marginTop: '4vh',
              marginBottom: 40,
              overflow: 'hidden',
            }}
          >
            <div style={{ padding: '16px 18px', borderBottom: '1px solid rgba(0,0,0,0.06)', display: 'flex', alignItems: 'flex-start', gap: 12 }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 11, fontWeight: 800, color: '#b8922e', letterSpacing: '0.1em', marginBottom: 6 }}>
                  {((previewEditMode ? previewDraft.categoria : previewRow.categoria) || '').toUpperCase()}
                </div>
                {previewEditMode ? (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                    <input
                      value={previewDraft.titulo}
                      onChange={e => setPreviewDraft(d => ({ ...d, titulo: e.target.value }))}
                      placeholder="Título"
                      style={{
                        width: '100%',
                        padding: '10px 12px',
                        borderRadius: 10,
                        border: '1px solid rgba(0,0,0,0.12)',
                        background: '#fff',
                        color: '#111',
                        fontSize: 14,
                        boxSizing: 'border-box',
                      }}
                    />
                    <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                      <select
                        value={previewDraft.categoria}
                        onChange={e => setPreviewDraft(d => ({ ...d, categoria: e.target.value }))}
                        style={{
                          padding: '10px 12px',
                          borderRadius: 10,
                          border: '1px solid rgba(0,0,0,0.12)',
                          background: '#fff',
                          color: '#111',
                          fontSize: 12,
                          cursor: 'pointer',
                        }}
                      >
                        {CAT_OPTIONS.map(c => (
                          <option key={c} value={c}>
                            {c}
                          </option>
                        ))}
                      </select>
                      <input
                        value={previewDraft.tiempo_lectura}
                        onChange={e => setPreviewDraft(d => ({ ...d, tiempo_lectura: e.target.value }))}
                        placeholder="Tiempo lectura (opcional)"
                        style={{
                          flex: '1 1 160px',
                          minWidth: 150,
                          padding: '10px 12px',
                          borderRadius: 10,
                          border: '1px solid rgba(0,0,0,0.12)',
                          background: '#fff',
                          color: '#111',
                          fontSize: 12,
                          boxSizing: 'border-box',
                        }}
                      />
                    </div>
                    <textarea
                      value={previewDraft.extracto}
                      onChange={e => setPreviewDraft(d => ({ ...d, extracto: e.target.value }))}
                      placeholder="Extracto (opcional)"
                      rows={2}
                      style={{
                        width: '100%',
                        padding: '10px 12px',
                        borderRadius: 10,
                        border: '1px solid rgba(0,0,0,0.12)',
                        background: '#fff',
                        color: '#111',
                        fontSize: 12,
                        boxSizing: 'border-box',
                        resize: 'vertical',
                      }}
                    />
                    <input
                      value={previewDraft.imagen_url}
                      onChange={e => setPreviewDraft(d => ({ ...d, imagen_url: e.target.value }))}
                      placeholder="Imagen URL (opcional)"
                      style={{
                        width: '100%',
                        padding: '10px 12px',
                        borderRadius: 10,
                        border: '1px solid rgba(0,0,0,0.12)',
                        background: '#fff',
                        color: '#111',
                        fontSize: 12,
                        boxSizing: 'border-box',
                      }}
                    />
                    {previewDraft.imagen_url?.trim() ? (
                      <div style={{ marginTop: 10 }}>
                        <div style={{ fontSize: 11, fontWeight: 700, color: '#444', marginBottom: 6 }}>
                          Procedencia de la imagen
                        </div>
                        <select
                          value={previewDraft.imagen_procedencia}
                          onChange={e =>
                            setPreviewDraft(d => ({ ...d, imagen_procedencia: e.target.value }))
                          }
                          style={{
                            width: '100%',
                            padding: '10px 12px',
                            borderRadius: 10,
                            border: '1px solid rgba(0,0,0,0.12)',
                            background: '#fff',
                            color: '#111',
                            fontSize: 12,
                            boxSizing: 'border-box',
                          }}
                        >
                          {NOTICIA_IMAGEN_PROCEDENCIA_OPTIONS.map(opt => (
                            <option key={opt.value} value={opt.value}>
                              {opt.label}
                            </option>
                          ))}
                        </select>
                        {previewDraft.imagen_procedencia === NOTICIA_IMAGEN_SOURCE.PEXELS ? (
                          <input
                            value={previewDraft.imagen_pexels_url}
                            onChange={e =>
                              setPreviewDraft(d => ({ ...d, imagen_pexels_url: e.target.value }))
                            }
                            placeholder="URL de la foto en Pexels (opcional)"
                            style={{
                              width: '100%',
                              marginTop: 8,
                              padding: '10px 12px',
                              borderRadius: 10,
                              border: '1px solid rgba(0,0,0,0.12)',
                              background: '#fff',
                              color: '#111',
                              fontSize: 12,
                              boxSizing: 'border-box',
                            }}
                          />
                        ) : null}
                        {previewDraft.imagen_procedencia === NOTICIA_IMAGEN_SOURCE.OTHER ? (
                          <input
                            value={previewDraft.imagen_procedencia_text}
                            onChange={e =>
                              setPreviewDraft(d => ({ ...d, imagen_procedencia_text: e.target.value }))
                            }
                            placeholder="p. ej. Rolling Stone, Wikipedia, nombre del medio…"
                            style={{
                              width: '100%',
                              marginTop: 8,
                              padding: '10px 12px',
                              borderRadius: 10,
                              border: '1px solid rgba(0,0,0,0.12)',
                              background: '#fff',
                              color: '#111',
                              fontSize: 12,
                              boxSizing: 'border-box',
                            }}
                          />
                        ) : null}
                      </div>
                    ) : null}
                  </div>
                ) : (
                  <h2 style={{ fontSize: '1.35rem', fontWeight: 750, margin: 0, fontFamily: 'Georgia,serif', lineHeight: 1.35, color: '#111' }}>
                    {previewRow.titulo}
                  </h2>
                )}
                <div style={{ marginTop: 8, fontSize: 12, color: '#888' }}>
                  {formatFechaCorta(previewRow.fecha_publicacion || previewRow.created_at)}
                  {(previewEditMode ? previewDraft.tiempo_lectura : previewRow.tiempo_lectura)?.trim()
                    ? ` · ${(previewEditMode ? previewDraft.tiempo_lectura : previewRow.tiempo_lectura).trim()}`
                    : ''}
                </div>
              </div>
              <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                {!previewEditMode ? (
                  <button
                    type="button"
                    disabled={previewSaving}
                    onClick={() => setPreviewEditMode(true)}
                    style={{
                      padding: '8px 12px',
                      borderRadius: 10,
                      border: '1px solid rgba(0,0,0,0.1)',
                      background: '#111',
                      color: '#fff',
                      cursor: 'pointer',
                      fontSize: 12,
                      fontWeight: 700,
                    }}
                    title="Editar antes de aprobar/publicar"
                  >
                    Editar
                  </button>
                ) : (
                  <>
                    <button
                      type="button"
                      disabled={previewSaving}
                      onClick={() => {
                        setPreviewEditMode(false);
                        const procedencia = getNoticiaImagenProcedencia(previewRow.metadata);
                        setPreviewDraft({
                          titulo: String(previewRow.titulo ?? ''),
                          extracto: String(previewRow.extracto ?? ''),
                          categoria: String(previewRow.categoria ?? 'Vinilo') || 'Vinilo',
                          tiempo_lectura: String(previewRow.tiempo_lectura ?? ''),
                          imagen_url: String(previewRow.imagen_url ?? ''),
                          contenido_html: String(previewRow.contenido_html ?? ''),
                          imagen_procedencia: procedencia.source,
                          imagen_procedencia_text: procedencia.attributionText,
                          imagen_pexels_url: procedencia.pexelsUrl,
                        });
                      }}
                      style={{
                        padding: '8px 12px',
                        borderRadius: 10,
                        border: '1px solid rgba(0,0,0,0.1)',
                        background: '#fafafa',
                        color: '#111',
                        cursor: 'pointer',
                        fontSize: 12,
                        fontWeight: 700,
                      }}
                      title="Descartar cambios"
                    >
                      Cancelar
                    </button>
                    <button
                      type="button"
                      disabled={previewSaving}
                      onClick={() => guardarEdicionVistaPrevia()}
                      style={{
                        padding: '8px 12px',
                        borderRadius: 10,
                        border: '1px solid rgba(29,158,117,0.35)',
                        background: previewSaving ? '#e9f7f1' : 'rgba(29,158,117,0.12)',
                        color: '#1d9e75',
                        cursor: previewSaving ? 'wait' : 'pointer',
                        fontSize: 12,
                        fontWeight: 800,
                      }}
                      title="Guardar cambios del borrador"
                    >
                      {previewSaving ? 'Guardando…' : 'Guardar'}
                    </button>
                  </>
                )}
              <button
                type="button"
                onClick={() => setPreviewRow(null)}
                aria-label="Cerrar"
                style={{
                  flexShrink: 0,
                  width: 36,
                  height: 36,
                  borderRadius: 10,
                  border: '1px solid rgba(0,0,0,0.1)',
                  background: '#fafafa',
                  cursor: 'pointer',
                  fontSize: 18,
                  lineHeight: 1,
                }}
              >
                ×
              </button>
              </div>
            </div>
            {(previewEditMode ? previewDraft.imagen_url : noticiaTieneImagenPreview(previewRow)) ? (
              <div style={{ background: '#0a0a0f' }}>
                {!previewEditMode && getNoticiaImagenOrigenLabel(previewRow) ? (
                  <div
                    style={{
                      padding: '6px 12px 0',
                      fontSize: 9,
                      letterSpacing: '0.06em',
                      textTransform: 'uppercase',
                      color: 'rgba(255,255,255,0.38)',
                      textAlign: 'right',
                    }}
                  >
                    Imagen: {getNoticiaImagenOrigenLabel(previewRow)}
                    {resolveNoticiaImagenPreviewUrl(previewRow).startsWith('data:')
                      ? ' · se sube al publicar'
                      : !String(previewRow.imagen_url ?? '').includes('/noticias-imagenes/')
                        ? ' · se guarda al publicar'
                        : ''}
                  </div>
                ) : null}
                <div style={{ maxHeight: 280, overflow: 'hidden' }}>
                  <img
                    src={
                      previewEditMode
                        ? previewDraft.imagen_url.trim()
                        : resolveNoticiaImagenPreviewUrl(previewRow)
                    }
                    alt=""
                    style={{ width: '100%', display: 'block', objectFit: 'cover', maxHeight: 280 }}
                  />
                </div>
                <NoticiaImagenCredit
                  variant="admin"
                  noticia={{
                    imagen_url: previewEditMode
                      ? previewDraft.imagen_url.trim()
                      : resolveNoticiaImagenPreviewUrl(previewRow),
                    metadata: previewEditMode
                      ? applyNoticiaImagenProcedencia(previewRow.metadata, previewDraft.imagen_url, {
                          source: previewDraft.imagen_procedencia,
                          attributionText: previewDraft.imagen_procedencia_text,
                          pexelsUrl: previewDraft.imagen_pexels_url,
                        })
                      : previewRow.metadata,
                  }}
                />
              </div>
            ) : null}
            <div
              style={{
                padding: '20px 22px 26px',
                fontSize: 15,
                lineHeight: 1.7,
                color: '#333',
              }}
              className="dv-noticia-body"
            >
              {previewEditMode ? (
                <div style={{ marginBottom: 14 }}>
                  <div style={{ fontSize: 12, fontWeight: 800, color: '#111', marginBottom: 8 }}>Contenido (HTML)</div>
                  <textarea
                    value={previewDraft.contenido_html}
                    onChange={e => setPreviewDraft(d => ({ ...d, contenido_html: e.target.value }))}
                    rows={10}
                    style={{
                      width: '100%',
                      padding: 12,
                      borderRadius: 12,
                      border: '1px solid rgba(0,0,0,0.12)',
                      background: '#fff',
                      color: '#111',
                      fontSize: 12,
                      fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace',
                      boxSizing: 'border-box',
                      resize: 'vertical',
                    }}
                  />
                  <div style={{ marginTop: 10, fontSize: 12, fontWeight: 800, color: '#111' }}>Vista previa saneada</div>
                  <div style={{ marginTop: 8, padding: 12, borderRadius: 12, border: '1px solid rgba(0,0,0,0.06)', background: '#fafafa' }}>
                    {safePreviewHtml ? <div dangerouslySetInnerHTML={{ __html: safePreviewHtml }} /> : <p style={{ color: '#888' }}>Sin contenido HTML.</p>}
                  </div>
                </div>
              ) : null}
              {safePreviewHtml ? (
                <div dangerouslySetInnerHTML={{ __html: safePreviewHtml }} />
              ) : (
                <p style={{ color: '#888' }}>Sin contenido HTML.</p>
              )}
            </div>
          </div>
        </div>
      ) : null}

      <AdminSocialKitModal
        open={!!socialKitOpen}
        onClose={() => setSocialKitOpen(null)}
        contentType="noticia"
        contentId={socialKitOpen?.contentId ?? ''}
        titleHint={socialKitOpen?.titleHint ?? ''}
        onError={onError}
        onSuccess={msg => {
          if (socialFlashTimerRef.current) clearTimeout(socialFlashTimerRef.current);
          setSocialFlash(msg || 'Kit de redes listo.');
          socialFlashTimerRef.current = setTimeout(() => setSocialFlash(''), 6000);
          onBadgesRefresh?.();
        }}
      />


      {uploadProcedenciaModal ? (
        <div
          role="dialog"
          aria-modal="true"
          aria-label="Procedencia de la imagen"
          style={{
            position: 'fixed',
            inset: 0,
            zIndex: 2010,
            background: 'rgba(0,0,0,0.72)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: 24,
          }}
          onClick={() => setUploadProcedenciaModal(null)}
        >
          <div role="presentation" onClick={e => e.stopPropagation()} style={{ width: '100%', maxWidth: 480 }}>
            <DCard style={{ padding: 18 }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12, gap: 12 }}>
                <div>
                  <div style={{ fontSize: 15, fontWeight: 700, color: '#fff' }}>Procedencia de la imagen</div>
                  <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.45)', marginTop: 4, lineHeight: 1.4 }}>
                    {uploadProcedenciaModal.row?.titulo
                      ? `«${String(uploadProcedenciaModal.row.titulo).slice(0, 80)}»`
                      : 'Borrador'}
                  </div>
                </div>
                <button
                  type="button"
                  aria-label="Cerrar"
                  onClick={() => setUploadProcedenciaModal(null)}
                  style={{
                    width: 32,
                    height: 32,
                    borderRadius: 8,
                    border: '0.5px solid rgba(255,255,255,0.2)',
                    background: 'rgba(255,255,255,0.06)',
                    color: '#fff',
                    cursor: 'pointer',
                    fontSize: 18,
                    lineHeight: 1,
                  }}
                >
                  ×
                </button>
              </div>
              <p style={{ fontSize: 11, color: 'rgba(255,255,255,0.5)', margin: '0 0 12px', lineHeight: 1.45 }}>
                Pega una URL (se descargará y comprimirá en nuestro servidor) o sube un archivo desde tu equipo. La procedencia se mostrará bajo la foto.
              </p>
              <label style={{ display: 'block', fontSize: 11, color: 'rgba(255,255,255,0.45)', marginBottom: 4 }}>
                URL de la imagen
              </label>
              <input
                value={uploadProcedenciaModal.imagenUrl}
                onChange={e =>
                  setUploadProcedenciaModal(m => (m ? { ...m, imagenUrl: e.target.value } : m))
                }
                placeholder="https://…"
                style={{ ...inputStyle, marginBottom: 10 }}
              />
              <label style={{ display: 'block', fontSize: 11, color: 'rgba(255,255,255,0.45)', marginBottom: 4 }}>
                Procedencia
              </label>
              <select
                value={uploadProcedenciaModal.source}
                onChange={e =>
                  setUploadProcedenciaModal(m => (m ? { ...m, source: e.target.value } : m))
                }
                style={{ ...inputStyle, marginBottom: 10, cursor: 'pointer' }}
              >
                {NOTICIA_IMAGEN_PROCEDENCIA_OPTIONS.map(opt => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
              {uploadProcedenciaModal.source === NOTICIA_IMAGEN_SOURCE.PEXELS ? (
                <>
                  <label style={{ display: 'block', fontSize: 11, color: 'rgba(255,255,255,0.45)', marginBottom: 4 }}>
                    URL en Pexels (opcional)
                  </label>
                  <input
                    value={uploadProcedenciaModal.pexelsUrl}
                    onChange={e =>
                      setUploadProcedenciaModal(m => (m ? { ...m, pexelsUrl: e.target.value } : m))
                    }
                    placeholder="https://www.pexels.com/photo/…"
                    style={{ ...inputStyle, marginBottom: 10 }}
                  />
                </>
              ) : null}
              {uploadProcedenciaModal.source === NOTICIA_IMAGEN_SOURCE.OTHER ? (
                <>
                  <label style={{ display: 'block', fontSize: 11, color: 'rgba(255,255,255,0.45)', marginBottom: 4 }}>
                    Texto de procedencia
                  </label>
                  <input
                    value={uploadProcedenciaModal.attributionText}
                    onChange={e =>
                      setUploadProcedenciaModal(m => (m ? { ...m, attributionText: e.target.value } : m))
                    }
                    placeholder="p. ej. Rolling Stone, Wikipedia…"
                    style={{ ...inputStyle, marginBottom: 10 }}
                  />
                  <p style={{ fontSize: 10, color: 'rgba(255,255,255,0.38)', margin: '0 0 10px', lineHeight: 1.4 }}>
                    Se mostrará como «Imagen de …» bajo la foto.
                  </p>
                </>
              ) : null}
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, justifyContent: 'flex-end', marginTop: 6 }}>
                <button
                  type="button"
                  disabled={uploadingImageId === uploadProcedenciaModal.row?.id}
                  onClick={() => setUploadProcedenciaModal(null)}
                  style={{
                    background: 'transparent',
                    color: 'rgba(255,255,255,0.55)',
                    border: '0.5px solid rgba(255,255,255,0.15)',
                    borderRadius: 12,
                    padding: '10px 18px',
                    fontWeight: 500,
                    fontSize: 12,
                    cursor: uploadingImageId === uploadProcedenciaModal.row?.id ? 'wait' : 'pointer',
                  }}
                >
                  Cancelar
                </button>
                <BtnGhost
                  type="button"
                  style={{ width: 'auto', minWidth: 140, fontSize: 12 }}
                  disabled={uploadingImageId === uploadProcedenciaModal.row?.id}
                  onClick={confirmarProcedenciaYSubirImagen}
                >
                  Elegir archivo
                </BtnGhost>
                {uploadProcedenciaModal.imagenUrl?.trim() ? (
                  <BtnGold
                    type="button"
                    style={{ width: 'auto', minWidth: 120 }}
                    disabled={uploadingImageId === uploadProcedenciaModal.row?.id}
                    onClick={() => guardarImagenUrlDesdeModal()}
                  >
                    {uploadingImageId === uploadProcedenciaModal.row?.id ? 'Guardando…' : 'Aceptar URL'}
                  </BtnGold>
                ) : null}
              </div>
            </DCard>
          </div>
        </div>
      ) : null}

      {promptImagenRow ? (
        <div
          role="dialog"
          aria-modal="true"
          aria-label="Prompt imagen"
          style={{
            position: 'fixed',
            inset: 0,
            zIndex: 2010,
            background: 'rgba(0,0,0,0.72)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: 24,
          }}
          onClick={() => setPromptImagenRow(null)}
        >
          <div role="presentation" onClick={e => e.stopPropagation()} style={{ width: '100%', maxWidth: 560 }}>
            <DCard style={{ padding: 18 }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12, gap: 12 }}>
                <div style={{ fontSize: 15, fontWeight: 700, color: '#fff' }}>Prompt imagen</div>
                <button
                  type="button"
                  aria-label="Cerrar"
                  onClick={() => setPromptImagenRow(null)}
                  style={{
                    width: 32,
                    height: 32,
                    borderRadius: 8,
                    border: '0.5px solid rgba(255,255,255,0.2)',
                    background: 'rgba(255,255,255,0.06)',
                    color: '#fff',
                    cursor: 'pointer',
                    fontSize: 18,
                    lineHeight: 1,
                  }}
                >
                  ×
                </button>
              </div>
              <textarea
                readOnly
                value={textoPromptImagen}
                style={{
                  ...textareaStyle,
                  width: '100%',
                  minHeight: 320,
                  fontSize: 12,
                  lineHeight: 1.55,
                  marginBottom: 14,
                  fontFamily: 'ui-monospace, Consolas, monospace',
                }}
                onFocus={e => e.target.select()}
              />
              <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 12 }}>
                <BtnGold type="button" style={{ width: 'auto', minWidth: 120 }} onClick={copiarPromptImagen}>
                  Copiar
                </BtnGold>
                {promptImagenCopiado ? (
                  <span style={{ fontSize: 12, fontWeight: 600, color: '#5DCAA5' }}>¡Copiado!</span>
                ) : null}
              </div>
            </DCard>
          </div>
        </div>
      ) : null}

      {showNewForm ? (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0,0,0,0.65)',
            zIndex: 1900,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: 20,
            overflowY: 'auto',
          }}
          onClick={() => !savingNew && setShowNewForm(false)}
        >
          <div role="presentation" onClick={e => e.stopPropagation()}>
          <DCard style={{ maxWidth: 520, width: '100%', padding: 20 }}>
            <div style={{ fontSize: 15, fontWeight: 700, color: '#fff', marginBottom: 14 }}>Nueva noticia manual</div>
            <label style={{ display: 'block', fontSize: 11, color: 'rgba(255,255,255,0.45)', marginBottom: 4 }}>Título</label>
            <input
              value={form.titulo}
              onChange={e => setForm(f => ({ ...f, titulo: e.target.value }))}
              style={inputStyle}
            />
            <label style={{ display: 'block', fontSize: 11, color: 'rgba(255,255,255,0.45)', marginBottom: 4, marginTop: 10 }}>Extracto</label>
            <textarea value={form.extracto} onChange={e => setForm(f => ({ ...f, extracto: e.target.value }))} rows={2} style={textareaStyle} />
            <label style={{ display: 'block', fontSize: 11, color: 'rgba(255,255,255,0.45)', marginBottom: 4, marginTop: 10 }}>Categoría</label>
            <select
              value={form.categoria}
              onChange={e => setForm(f => ({ ...f, categoria: e.target.value }))}
              style={{ ...inputStyle, cursor: 'pointer' }}
            >
              {CAT_OPTIONS.map(c => (
                <option key={c} value={c}>
                  {c}
                </option>
              ))}
            </select>
            <label style={{ display: 'block', fontSize: 11, color: 'rgba(255,255,255,0.45)', marginBottom: 4, marginTop: 10 }}>URL imagen</label>
            <input
              value={form.imagen_url}
              onChange={e => setForm(f => ({ ...f, imagen_url: e.target.value }))}
              style={inputStyle}
              placeholder="https://…"
            />
            {form.imagen_url.trim() ? (
              <>
                <label style={{ display: 'block', fontSize: 11, color: 'rgba(255,255,255,0.45)', marginBottom: 4, marginTop: 10 }}>
                  Procedencia de la imagen
                </label>
                <select
                  value={form.imagen_procedencia}
                  onChange={e => setForm(f => ({ ...f, imagen_procedencia: e.target.value }))}
                  style={{ ...inputStyle, cursor: 'pointer' }}
                >
                  {NOTICIA_IMAGEN_PROCEDENCIA_OPTIONS.map(opt => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
                {form.imagen_procedencia === NOTICIA_IMAGEN_SOURCE.PEXELS ? (
                  <>
                    <label style={{ display: 'block', fontSize: 11, color: 'rgba(255,255,255,0.45)', marginBottom: 4, marginTop: 10 }}>
                      URL en Pexels (opcional)
                    </label>
                    <input
                      value={form.imagen_pexels_url}
                      onChange={e => setForm(f => ({ ...f, imagen_pexels_url: e.target.value }))}
                      style={inputStyle}
                      placeholder="https://www.pexels.com/photo/…"
                    />
                  </>
                ) : null}
                {form.imagen_procedencia === NOTICIA_IMAGEN_SOURCE.OTHER ? (
                  <>
                    <label style={{ display: 'block', fontSize: 11, color: 'rgba(255,255,255,0.45)', marginBottom: 4, marginTop: 10 }}>
                      Texto de procedencia
                    </label>
                    <input
                      value={form.imagen_procedencia_text}
                      onChange={e => setForm(f => ({ ...f, imagen_procedencia_text: e.target.value }))}
                      style={inputStyle}
                      placeholder="p. ej. Rolling Stone, Wikipedia…"
                    />
                  </>
                ) : null}
              </>
            ) : null}
            <label style={{ display: 'block', fontSize: 11, color: 'rgba(255,255,255,0.45)', marginBottom: 4, marginTop: 10 }}>Tiempo de lectura</label>
            <input
              value={form.tiempo_lectura}
              onChange={e => setForm(f => ({ ...f, tiempo_lectura: e.target.value }))}
              style={inputStyle}
              placeholder="p. ej. 3 min"
            />
            <label style={{ display: 'block', fontSize: 11, color: 'rgba(255,255,255,0.45)', marginBottom: 4, marginTop: 10 }}>Contenido HTML</label>
            <textarea
              value={form.contenido_html}
              onChange={e => setForm(f => ({ ...f, contenido_html: e.target.value }))}
              rows={8}
              style={textareaStyle}
              placeholder="<p>…</p>"
            />
            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 16 }}>
              <button
                type="button"
                disabled={savingNew}
                style={{
                  background: 'transparent',
                  color: 'rgba(255,255,255,0.55)',
                  border: '0.5px solid rgba(255,255,255,0.15)',
                  borderRadius: 12,
                  padding: '10px 18px',
                  fontWeight: 500,
                  fontSize: 12,
                  cursor: savingNew ? 'wait' : 'pointer',
                }}
                onClick={() => setShowNewForm(false)}
              >
                Cancelar
              </button>
              <BtnGold type="button" style={{ width: 'auto', minWidth: 140 }} disabled={savingNew} onClick={submitNew}>
                {savingNew ? 'Guardando…' : 'Crear borrador'}
              </BtnGold>
            </div>
          </DCard>
          </div>
        </div>
      ) : null}
    </div>
  );
}

const inputStyle = {
  width: '100%',
  padding: '10px 12px',
  borderRadius: 10,
  border: '0.5px solid rgba(255,255,255,0.15)',
  background: 'rgba(255,255,255,0.06)',
  color: '#fff',
  fontSize: 13,
  fontFamily: 'inherit',
  boxSizing: 'border-box',
};

const textareaStyle = {
  ...inputStyle,
  resize: 'vertical',
  minHeight: 80,
};
